/**
 * Tenant / company logo URL helpers.
 *
 * Logos live in R2 and are served from https://assets.vhire.com. When
 * Cloudflare Image Resizing ("Transformations") is enabled on the vhire.com
 * zone, we can request a resized, format-negotiated variant via the
 * `/cdn-cgi/image/` path — normalizing an arbitrary-size upload down to the
 * small box a logo is actually rendered in, and serving WebP/AVIF where the
 * browser supports it.
 *
 * Gated by `NEXT_PUBLIC_CF_IMAGE_RESIZE` so the transform only activates once
 * the zone setting is on. While off (the default) logos are served raw —
 * exactly today's behavior — so this is safe to ship before the zone is
 * flipped. Activation: enable Transformations on the vhire.com zone, then set
 * NEXT_PUBLIC_CF_IMAGE_RESIZE=true and redeploy the frontend.
 */

const ASSETS_HOST = 'assets.vhire.com';
const TRANSFORM_PREFIX = '/cdn-cgi/image/';

// Build-time flag (NEXT_PUBLIC_* is inlined at build time). Default ON since
// Cloudflare Transformations was enabled on the vhire.com zone (2026-06-16).
// Set NEXT_PUBLIC_CF_IMAGE_RESIZE=false to disable (emergency kill-switch — emits
// raw URLs) if the zone setting is ever turned off, otherwise /cdn-cgi/image/
// requests would 404 against the R2 asset worker. Read at call time so it stays
// inlined in prod yet remains stubbable in tests.
function isResizeEnabled(): boolean {
  return process.env.NEXT_PUBLIC_CF_IMAGE_RESIZE !== 'false';
}

export interface LogoResizeOptions {
  /** Target box width in device px. Request ~2x the CSS size for retina. */
  width: number;
  /** Target box height in device px. */
  height: number;
  /** Cloudflare fit mode. Default 'contain' (preserve aspect, no crop, no pad). */
  fit?: 'contain' | 'scale-down' | 'cover' | 'pad';
  /** Output quality 1–100. Default 85 (Cloudflare's default). */
  quality?: number;
}

/**
 * Wrap a logo URL in a Cloudflare Image Resizing transform when it is safe to
 * do so. Returns the URL unchanged when:
 *  - the resize flag is off (zone not yet enabled),
 *  - the URL is empty / relative / unparseable,
 *  - the host is not assets.vhire.com — same-zone transforms only; we don't
 *    enable `open` mode, so foreign hosts (e.g. ATS S3 logos) can't be
 *    transformed and must pass through, or
 *  - the source is an SVG — Cloudflare ignores resize params for SVG, and it
 *    scales losslessly client-side anyway, or
 *  - the URL is already a transform URL (defensive — avoids nested wraps).
 */
export function cfLogoUrl(
  url: string | null | undefined,
  opts: LogoResizeOptions,
): string {
  if (!url) return '';
  if (!isResizeEnabled()) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url; // relative or malformed — leave as-is
  }

  if (parsed.hostname !== ASSETS_HOST) return url;
  if (parsed.pathname.startsWith(TRANSFORM_PREFIX)) return url; // already transformed
  if (/\.svg$/i.test(parsed.pathname)) return url;

  const fit = opts.fit ?? 'contain';
  const quality = opts.quality ?? 85;
  const params = `width=${opts.width},height=${opts.height},fit=${fit},format=auto,quality=${quality}`;
  // Same-zone source: reference by path (no need to repeat the host). Preserve
  // any query string (e.g. ?v= cache-bust) so it reaches the origin fetch.
  return `https://${ASSETS_HOST}/cdn-cgi/image/${params}${parsed.pathname}${parsed.search}`;
}
